import { useEffect } from "react";

/**
 * Actualiza meta tags del <head> para SEO / redes sociales.
 * Etiqueta cada tag manejado con data-managed="seo" para poder limpiarlo entre navegaciones.
 *
 * Nota: los crawlers sociales (Facebook, WhatsApp) leen HTML estático,
 * por lo que estas etiquetas se ven en el navegador y en Googlebot (que ejecuta JS),
 * pero para vistas previas ricas sin SSR se necesita una etapa de pre-render.
 */
const MANAGED_ATTR = "data-managed";
const MANAGED_VALUE = "seo";

function upsertMetaByProperty(prop, content) {
  if (!content) return;
  let tag = document.head.querySelector(`meta[property="${prop}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", prop);
    document.head.appendChild(tag);
  }
  tag.setAttribute(MANAGED_ATTR, MANAGED_VALUE);
  tag.setAttribute("content", content);
}

function upsertMetaByName(name, content) {
  if (!content) return;
  let tag = document.head.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute(MANAGED_ATTR, MANAGED_VALUE);
  tag.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute(MANAGED_ATTR, MANAGED_VALUE);
  tag.setAttribute("href", href);
}

function clearManaged() {
  // Only remove tags that we specifically added and don't have a permanent semantic role.
  // For meta description/keywords/canonical we keep the tag but remove attributes; simpler to just remove and let useSEO re-add.
  const nodes = document.head.querySelectorAll(`meta[${MANAGED_ATTR}="${MANAGED_VALUE}"][property^="article:tag"]`);
  nodes.forEach((n) => n.parentNode?.removeChild(n));
  // Remove old json-ld tags managed by us
  const lds = document.head.querySelectorAll(`script[${MANAGED_ATTR}="${MANAGED_VALUE}"]`);
  lds.forEach((n) => n.parentNode?.removeChild(n));
}

function upsertJsonLd(id, data) {
  if (!data) return;
  const testId = `seo-jsonld-${id}`;
  let tag = document.head.querySelector(`script[data-jsonld-id="${id}"]`);
  if (!tag) {
    tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.setAttribute("data-jsonld-id", id);
    tag.setAttribute(MANAGED_ATTR, MANAGED_VALUE);
    tag.setAttribute("data-testid", testId);
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(data);
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  image,
  og,
  twitter,
  jsonLd,
  facebookAppId,
  extraLinks,
} = {}) {
  useEffect(() => {
    // Clean previous managed tags
    clearManaged();

    if (title) document.title = title;

    if (description) upsertMetaByName("description", description);
    if (keywords && keywords.length) upsertMetaByName("keywords", Array.isArray(keywords) ? keywords.join(", ") : keywords);
    if (canonical) upsertLink("canonical", canonical);

    // Open Graph
    if (og || title || image) {
      const o = og || {};
      upsertMetaByProperty("og:type", o.type || "website");
      upsertMetaByProperty("og:title", o.title || title);
      upsertMetaByProperty("og:description", o.description || description);
      upsertMetaByProperty("og:url", o.url || canonical || (typeof window !== "undefined" ? window.location.href : ""));
      upsertMetaByProperty("og:image", o.image || image);
      if (o.image_secure_url) upsertMetaByProperty("og:image:secure_url", o.image_secure_url);
      if (o.image_type) upsertMetaByProperty("og:image:type", o.image_type);
      if (o.image_width) upsertMetaByProperty("og:image:width", String(o.image_width));
      if (o.image_height) upsertMetaByProperty("og:image:height", String(o.image_height));
      if (o.image_alt) upsertMetaByProperty("og:image:alt", o.image_alt);
      upsertMetaByProperty("og:site_name", o.site_name || "Sur Primicia");
      upsertMetaByProperty("og:locale", o.locale || "es_AR");
      if (o.article_published_time) upsertMetaByProperty("article:published_time", o.article_published_time);
      if (o.article_modified_time) upsertMetaByProperty("article:modified_time", o.article_modified_time);
      if (o.article_section) upsertMetaByProperty("article:section", o.article_section);
      if (o.article_tag && Array.isArray(o.article_tag)) {
        // Only add first few
        o.article_tag.slice(0, 6).forEach((t) => {
          const el = document.createElement("meta");
          el.setAttribute("property", "article:tag");
          el.setAttribute("content", t);
          el.setAttribute(MANAGED_ATTR, MANAGED_VALUE);
          document.head.appendChild(el);
        });
      }
    }

    // Twitter Cards
    const tw = twitter || {};
    upsertMetaByName("twitter:card", tw.card || "summary_large_image");
    if (tw.site) upsertMetaByName("twitter:site", tw.site);
    upsertMetaByName("twitter:title", tw.title || title);
    upsertMetaByName("twitter:description", tw.description || description);
    upsertMetaByName("twitter:image", tw.image || image);
    if (tw.image_alt) upsertMetaByName("twitter:image:alt", tw.image_alt);

    if (facebookAppId) upsertMetaByProperty("fb:app_id", facebookAppId);

    if (extraLinks) {
      extraLinks.forEach((l) => upsertLink(l.rel, l.href));
    }

    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      arr.forEach((data, i) => upsertJsonLd(`main-${i}`, data));
    }

    return () => {
      // On unmount, don't clean — the next useSEO will reset.
    };
  }, [title, description, keywords, canonical, image, JSON.stringify(og || null), JSON.stringify(twitter || null), JSON.stringify(jsonLd || null), facebookAppId, JSON.stringify(extraLinks || null)]);
}
